<docs>
  # ui-horizontal
  > Horizontal animations on scroll with custom gsap
  
  @author Kevin Bastien <kevin@kffein.com>
</docs>
  
<script lang="ts">
import { defineComponent, reactive, toRefs, PropType } from 'vue';
import { templateRef, useElementSize, debouncedWatch } from '@vueuse/core';
import { until } from '@vueuse/shared';
import { gsap, ScrollTrigger } from 'vendors/gsap';
import useFlow from 'utils/flow';
import { store as InterfaceStore } from 'plugins/store/interface';
import { store as scrollStore } from 'components/SmoothScroll/store';

interface Data {
  tlAnimHori?: O<gsap.core.Timeline>,
  tlScroll?: ScrollTrigger,
}
  
export default defineComponent({
  name: "UiHorizontal",
  props: {
    container: {
      type: Boolean,
      default: true,
    },
    /**
      * Speed of horizontal scroll, 1 = same speed as the main scroll, 0.5 = faster, 1.5 = slower
    */
    speed: {
      type: Number,
      default: 1.5,
    },
    /**
      * scrub for scroll animation in second, default 0 = same speed as scroll aka lag animation to catch up scroll
    */
    scrub: {
      type: Number,
      default: 0,
    },
    /**
      * Pass a gsap timeline to be synced with our horizontal timeline.
    */
    animation: {
      type: Object as PropType<O<gsap.core.Timeline>>,
      default: undefined,
    },
    /**
      * when passing an `animation` props, check if we need to round up animation total duration
    */
    roundDuration: {
      type: Boolean,
      default: true,
    }
  },
  emits: ['progress'],
  setup(props, {emit}) {
    const el = templateRef<HTMLElement | null>('el', null)
    const container = templateRef<HTMLElement | null>('container', null)
    const scroll = templateRef<HTMLElement | null>('scroll', null)

    const scrollSize = useElementSize(scroll)
    const containerSize = useElementSize(container)

    const { ready } = toRefs(scrollStore.state);
    const { viewport } = toRefs(InterfaceStore.state);

    const data: Data = reactive({
      tlAnimHori: undefined,
      tlScroll: undefined,
    });

    const { onInitUi } = useFlow()

    onInitUi(() => {
      if (InterfaceStore.isLayoutDesktop) {
        setTimeout(() => {
          setHorizontalScroll();
        }, 500);
      }
    });

    debouncedWatch(
      viewport,
      () => {
        if (data.tlAnimHori) {
          destroyHorizontalScroll()
        }

        if (InterfaceStore.isLayoutDesktop) {
          setHorizontalScroll();
        }
      },
      { debounce: 250, deep: true }
    )

    const setHorizontalScroll = () => {
      until(ready).toBe(true).then(() => {
        if (scroll.value) {
          const element = scroll.value as HTMLElement;
          gsap.set(element, {clearProps: 'all'});
          data.tlAnimHori = gsap.timeline()
            .to(element, {
              xPercent: 0,
              x: () => {
                return -(scrollSize.width.value - (containerSize?.width.value * 1)) + "px"
              },
              ease: 'none',
              duration: 1,
            }, '0');

          if (props.animation) {
            if (props.roundDuration) {
              props.animation.totalDuration(1);
            }
              data.tlAnimHori.add(props.animation, 0);
          }

          data.tlScroll = ScrollTrigger.create({
            trigger: el.value,
            start: 'top top',
            end: () => {
              // return `+=${scrollSize.width.value * 0.5}`; // ratio scrolll is 1/0.5, we substract 50% scrollwidth aka faster scroll
              // return `+=${scrollSize.width.value * 1}`; ratio scroll 1/1
              // return `+=${scrollSize.width.value * 1.5}`; // ratio scrolll is 1/1.5, we add 50% scrollwidth aka slower scroll
              return `+=${scrollSize.width.value * props.speed}`; // based on props
            },
            onUpdate: (st) => {
              emit('progress', st.progress);
            },
            pin: true,
            // will help every other scrolltrigger to actually update with this
            // aka when we have a horizontal cpn in the middle of our page.
            anticipatePin: 1,
            refreshPriority: 1,
            // pinType: 'transform',
            // pinSpacing: true,
            scrub: props.scrub,
            animation: data.tlAnimHori
          });

        }
      })
    }

    const destroyHorizontalScroll = () => {
      if (data.tlScroll && scroll.value) {
        data.tlScroll.kill(true);
        data.tlScroll = undefined;
        emit('progress', 0);

        gsap.set(scroll.value, {clearProps: true});
      }
    }
  },
});
</script>
    
<template>
  <div ref="el" class="horizontal">
    <div ref="container" :class="container ? '_container' : ''">
      <div ref="scroll" class="scroll">
        <slot />
      </div>
    </div>
  </div>
</template>
    
<style lang="stylus" scoped>
  .horizontal  
    overflow hidden

  .scroll
    position relative
    width 100%
    f(row, $align: center, $wrap: wrap)
    z-index 20

    +layout(desktop)
      flex-wrap nowrap
      width fit-content
      // transform translate3d(0, 0, 0)
      min-height var(--viewport-innerHeight)
</style>
  